import type { Turn } from '@strategy4kids/protocol';
import { colorOf, type Placement } from './coords.js';

/**
 * Generates the squares a piece standing on `i` attacks. One generator per
 * variant — chess passes `squaresAttacked`, checkers passes
 * `checkersSquaresAttacked`.
 *
 * The seam exists because the two games' movement models are genuinely
 * different and the shared chess attack table cannot express checkers: in
 * `attack-table.ts` the slider branch is
 * `type === 'r' ? ROOK_DIRS : type === 'b' ? BISHOP_DIRS : [...ROOK_DIRS, ...BISHOP_DIRS]`,
 * so a checkers man ('m') or king ('x') matches neither and falls through to
 * **flying-queen movement**. A man would appear to defend eight squares in
 * every direction at any distance, which is exactly the kind of wrong a kid
 * would read as truth.
 */
export type AttackGen = (placement: Placement, i: number) => number[];

/**
 * The safety picture for one player's pieces, keyed by board index.
 *
 * Both maps are relative to `forColor` — they describe *that player's* pieces,
 * not absolute board properties. A square appears in `defended` only when at
 * least one friendly piece attacks it, and in `threatened` only when at least
 * one enemy piece attacks it. Squares with no defenders and no attackers are
 * absent entirely, so callers rendering "every shield" can iterate the map
 * directly instead of filtering all 64 squares.
 *
 * Lookup a specific square with `defended.get(i) ?? []`.
 */
export interface ShieldMap {
  /** Friendly piece square → the squares of the friendly pieces defending it. */
  readonly defended: ReadonlyMap<number, readonly number[]>;
  /** Friendly piece square → the squares of the enemy pieces attacking it. */
  readonly threatened: ReadonlyMap<number, readonly number[]>;
}

export interface ShieldOptions {
  /**
   * Whether a king counts as a genuine defender of an adjacent friendly piece.
   *
   * Default **false**, deliberately. A king sitting next to a piece does not
   * really protect it: the king may only recapture if the square is otherwise
   * unattacked, so a "shield" from the king routinely means "you are already in
   * check". Chess.com and Lichess both count it; teaching apps generally
   * should not, because "the king is nearby so my piece is safe" is a standard
   * beginner blunder. It also floods the board — at the start position the
   * white king is adjacent to four of your own pieces.
   *
   * The start position is the clean demonstration: the e1 king attacks d2, e2
   * and f2, so counting it makes the famously "undefended" f2 pawn look safe.
   * Flip this to `true` for the strict geometric reading.
   */
  readonly kingDefends?: boolean;
}

const EMPTY: readonly number[] = Object.freeze([]);

/**
 * Build the reverse attack map in one pass and split it by colour.
 *
 * `buildAttackTable` (attack-table.ts) already computes "is this square
 * attacked by white / by black", but it collapses to a boolean per colour, so
 * it cannot answer *which* piece is doing the defending — which is the whole
 * point of a shield indicator. This walks the same edges once and keeps the
 * source index, so the reverse direction is free.
 *
 * Cost: O(occupied × attacked), which on a full chess board is 32 pieces ×
 * roughly 8 targets ≈ 260 edges. Cheap enough to recompute on every position
 * change, which is what the store does.
 *
 * Defence semantics are the pseudo-legal ones the generator already uses: a
 * pawn defends the squares it attacks (diagonally), never the square straight
 * in front of it; a slider defends the first square it reaches, occupied or
 * not, and stops there. Pins and check are deliberately NOT modelled — a
 * pinned piece still counts as a defender here, matching how the rest of the
 * engine's attack table already reasons. Kings are excluded by default; see
 * `ShieldOptions`.
 */
export function buildShieldMap(
  placement: Placement,
  attacks: AttackGen,
  forColor: Turn,
  options: ShieldOptions = {},
): ShieldMap {
  const kingDefends = options.kingDefends ?? false;
  const isKing = (char: string): boolean => char.toLowerCase() === 'k';

  // target square → indices of the pieces attacking it
  const attackersBy = new Map<number, number[]>();
  for (let i = 0; i < placement.length; i++) {
    if (placement[i] == null) continue;
    for (const target of attacks(placement, i)) {
      const list = attackersBy.get(target);
      if (list) list.push(i);
      else attackersBy.set(target, [i]);
    }
  }

  const defended = new Map<number, number[]>();
  const threatened = new Map<number, number[]>();

  for (let i = 0; i < placement.length; i++) {
    const char = placement[i];
    if (char == null) continue;
    // Only report on the viewer's own pieces. The map is "how safe are my
    // things", not "what does the whole board look like".
    if (colorOf(char) !== forColor) continue;

    const attackers = attackersBy.get(i);
    if (attackers == null) continue;

    const friendly: number[] = [];
    const enemy: number[] = [];
    for (const src of attackers) {
      const srcChar = placement[src];
      if (srcChar == null) continue;
      if (colorOf(srcChar) === forColor) {
        // The king filter applies to defence only. An enemy king bearing down
        // on your piece is real information and stays in the threat list.
        if (!kingDefends && isKing(srcChar)) continue;
        friendly.push(src);
      } else {
        enemy.push(src);
      }
    }
    if (friendly.length > 0) defended.set(i, friendly);
    if (enemy.length > 0) threatened.set(i, enemy);
  }

  return { defended, threatened };
}

/** Friendly defenders of `index`, or an empty list. */
export function defendersOf(map: ShieldMap, index: number): readonly number[] {
  return map.defended.get(index) ?? EMPTY;
}

/** Enemy attackers of `index`, or an empty list. */
export function threatsTo(map: ShieldMap, index: number): readonly number[] {
  return map.threatened.get(index) ?? EMPTY;
}
